import React, { useState, useEffect, useCallback } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import Table from './components/Table/Table';
import FilterBar from './components/FilterBar/FilterBar';
import CheckboxMultiSelect from '../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import DateFilter from './components/Filters/DateFilter';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import {getAllPedidosByLocalIdAndRangeDate } from '../../../../crud/pedidos_api/pedidos';
import {
  getMesasOptions,
  getUsersOptions,
  getPedidosTypeOptions,
  UsersOption,
  MesasOption,
  PedidoTypeOption,
} from './components/RenderFunctions';
import { exportFilteredPedidos } from './utils/pedidosExcelExport';
import { Pedido } from '../types';
import { MPPageTitle } from '../../../../components/ui';
import { LocalSession } from '../../../../types/LocalSession';


interface HistorialPedidosProps {
  lastSession?: LocalSession | null;
}

const HistorialPedidos: React.FC<HistorialPedidosProps> = ({ lastSession }) => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [filteredPedidos, setFilteredPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState(true);

  // Fecha local: Por defecto sesión actual (hoy)
  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const today = new Date();
  // Always keep a wide date range for API (2 days ago to tomorrow)
  // This ensures we fetch all potential session data
  const [startDate, setStartDate] = useState<string>(addDays(today, -2));
  const [endDate, setEndDate] = useState<string>(addDays(today, 1));

  // Opciones para filtros
  const [users, setUsers] = useState<UsersOption[]>([]);
  const [mesas, setMesas] = useState<MesasOption[]>([]);
  const [pedidosType, setPedidosType] = useState<PedidoTypeOption[]>([]);

  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [selectedMesas, setSelectedMesas] = useState<any[]>([]);
  const [selectedPedidosType, setSelectedPedidosType] = useState<any[]>([]);
  const [selectedProductos, setSelectedProductos] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState('');

  // Session filter - disabled by default (will be fixed in the future)
  const [soloSesionActual, setSoloSesionActual] = useState<boolean>(false);
  
  const [showFilters, setShowFilters] = useState(false);

  // Leer localId
  const [localId, setLocalId] = useState<number>(0);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setLocalId(user.local_id || 0);
  }, []);

  // Date change handlers (no desactivamos soloSesionActual para paridad con Receipts)
  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
  };

  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
  };

  // Helper de depuración manual (sin logs automáticos). Ejecutar en consola: window.__debugSessionMods()
  useEffect(() => {
    (window as any).__debugSessionMods = () => {
      const counts: Record<string, number> = {};
      pedidos.forEach(p => {
        const sid = String((p as any).session_id ?? (p as any).local_session_id ?? 'none');
        counts[sid] = (counts[sid] || 0) + 1;
      });
      return counts;
    };
    return () => { delete (window as any).__debugSessionMods; };
  }, [pedidos, lastSession, soloSesionActual]);

  // Cargar pedidos por localId y rango de fechas
  useEffect(() => {
    if (!localId) return;
    setLoading(true);
    getAllPedidosByLocalIdAndRangeDate(localId, startDate, endDate)
      .then((response) => {
        const data = response.data.reverse();
        setPedidos(data);
      })
      .catch((error) => console.error('Error fetching pedidos:', error))
      .finally(() => setLoading(false));
  }, [localId, startDate, endDate]);

  // Cargar opciones multi-select
  useEffect(() => {
    (async () => {
      setUsers(await getUsersOptions());
      setMesas(await getMesasOptions());
      setPedidosType(await getPedidosTypeOptions());
    })();
  }, []);

  // Extraer opciones únicas de productos desde todos los pedidos (desde selections)
  const getProductosOptions = useCallback(() => {
    const productosSet = new Set<string>();
    pedidos.forEach(pedido => {
      if (pedido.selections && Array.isArray(pedido.selections)) {
        pedido.selections.forEach(selection => {
          if (selection.item_name) {
            productosSet.add(selection.item_name);
          }
        });
      }
    });
    return Array.from(productosSet)
      .sort()
      .map(producto => ({ value: producto, label: producto }));
  }, [pedidos]);

  // Función de filtros combinados (incluye fecha y sesión) - sesión igual que Receipts (por session_id, no timestamps)
  const applyFilters = useCallback(() => {
    let filtered = pedidos.filter((p) => {
      // Siempre respetamos el rango de fechas provisto (igual que Receipts usa rango del fetch)
      const fechaDate = new Date(p.timestamp);
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');
      if (!(fechaDate >= start && fechaDate <= end)) return false;

      const okUser =
        selectedUsers.length === 0 ||
        selectedUsers.some((o) => o.value === p.user_id);
      const okMesa =
        selectedMesas.length === 0 ||
        selectedMesas.some((o) => o.value === p.mesa_id);
      const okTipo =
        selectedPedidosType.length === 0 ||
        selectedPedidosType.some((o) => o.value === p.pedido_type_id);
      
      // Filtro de productos: verifica si alguna selection tiene un item_name en selectedProductos
      const okProducto =
        selectedProductos.length === 0 ||
        (p.selections && p.selections.some(sel =>
          selectedProductos.some(prod => prod.value === sel.item_name)
        ));

      return okUser && okMesa && okTipo && okProducto;
    });

    // Filtro sesión actual: comparar session_id/local_session_id contra lastSession.local_session_id
    if (soloSesionActual && lastSession?.local_session_id) {
      const targetSessionId = lastSession.local_session_id;
      filtered = filtered.filter(p => {
        const pedidoSessionId = (p as any).session_id ?? (p as any).local_session_id;
        return pedidoSessionId === targetSessionId;
      });
    }

    // Apply search filter
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter((p: any) => {
        const mesaNum = String(p.mesa_num || '').toLowerCase();
        const userName = String(p.user_name || '').toLowerCase();
        const clientName = String(p.client_name || '').toLowerCase();
        
        return mesaNum.includes(searchLower) || 
               userName.includes(searchLower) || 
               clientName.includes(searchLower);
      });
    }

    setFilteredPedidos(filtered);
  }, [pedidos, startDate, endDate, selectedUsers, selectedMesas, selectedPedidosType, selectedProductos, soloSesionActual, lastSession, searchValue]);

  // Reaplicar filtros cuando cambian dependencias
  useEffect(() => {
    applyFilters();
  }, [pedidos, startDate, endDate, selectedUsers, selectedMesas, selectedPedidosType, selectedProductos, soloSesionActual, lastSession, searchValue, applyFilters]);

  // Handle Excel export
  const handleExportToExcel = () => {
    if (!filteredPedidos || filteredPedidos.length === 0) {
      alert('No hay pedidos para exportar');
      return;
    }

    try {
      exportFilteredPedidos(filteredPedidos, startDate, endDate);
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      console.error('Error exporting pedidos to Excel:', error);
      toast.error('Error al exportar a Excel. Por favor, intenta nuevamente.');
    }
  };

  // Build pedido type ID → name map for the detail modal
  const pedidosTypeMap: Record<number, string> = {};
  pedidosType.forEach((p) => { pedidosTypeMap[p.id] = p.name; });

  // Optimistic cancel: remove pedido from local state without refetching
  const handlePedidoCancelled = (pedidoId: number) => {
    setPedidos(prev => prev.map(p => 
      p.pedido_id === pedidoId ? { ...p, cancelled: true } : p
    ));
  };

  const usersOptions = users.map((u) => ({ value: u.id, label: u.name }));
  const mesasOptions = mesas.map((m) => ({ value: m.id, label: m.num }));
  const pedidosTypeOptions = pedidosType.map((p) => ({ value: p.id, label: p.name }));
  const productosOptions = getProductosOptions();
  
  return (
    <div className="px-4 lg:px-6 pt-4">
      <MPPageTitle>Historial pedidos</MPPageTitle>

      {/* New FilterBar component matching Recibos style */}
      <FilterBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        soloSesionActual={soloSesionActual}
        onSoloSesionActualChange={setSoloSesionActual}
        usersOptions={usersOptions}
        selectedUsers={selectedUsers}
        onUsersChange={setSelectedUsers}
        mesasOptions={mesasOptions}
        selectedMesas={selectedMesas}
        onMesasChange={setSelectedMesas}
        tipoOptions={pedidosTypeOptions}
        selectedTipo={selectedPedidosType}
        onTipoChange={setSelectedPedidosType}
        productosOptions={productosOptions}
        selectedProductos={selectedProductos}
        onProductosChange={setSelectedProductos}
        onExportExcel={handleExportToExcel}
        loading={loading}
        dataCount={filteredPedidos.length}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />

      {/* Offcanvas para móvil - unified .mp-offcanvas pattern */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        className="mp-offcanvas historial-offcanvas"
        backdrop={true}
        scroll={false}
      >
        <Offcanvas.Header className="offcanvas-header">
          <div className="header-content">
            <Offcanvas.Title className="offcanvas-title">Opciones de Filtrado</Offcanvas.Title>
            <div className="section-indicator">Pedidos</div>
          </div>
          <Button
            variant="none"
            className="close-button"
            onClick={() => setShowFilters(false)}
            aria-label="Cerrar filtros"
          >
            <X size={24} />
          </Button>
        </Offcanvas.Header>

        <Offcanvas.Body className="offcanvas-body">
          <div className="filters-container">
            <div className="filter-section">
              <h6 className="filter-section-title">Filtros Generales</h6>

              <div className="filter-group">
                <div className="receipt-date-filter-container">
                  <DateFilter
                    startDate={startDate}
                    setStartDate={handleStartDateChange}
                    endDate={endDate}
                    setEndDate={handleEndDateChange}
                    soloSesionActual={soloSesionActual}
                  />
                </div>

                <div className="multiselect-container">
                  <div className="mobile-session-toggle">
                    <input
                      type="checkbox"
                      className="mobile-toggle-checkbox"
                      checked={soloSesionActual}
                      onChange={(e) => setSoloSesionActual(e.target.checked)}
                      id="soloSesionActualMobile"
                    />
                    <label className="mobile-toggle-label" htmlFor="soloSesionActualMobile">
                      Sesión actual
                    </label>
                  </div>
                </div>

                <div className="multiselect-container">
                  <CheckboxMultiSelect
                    options={usersOptions}
                    value={selectedUsers}
                    onChange={setSelectedUsers}
                    placeholder="Usuarios"
                  />
                </div>

                <div className="multiselect-container">
                  <CheckboxMultiSelect
                    options={mesasOptions}
                    value={selectedMesas}
                    onChange={setSelectedMesas}
                    placeholder="Mesas"
                  />
                </div>

                <div className="multiselect-container">
                  <CheckboxMultiSelect
                    options={pedidosTypeOptions}
                    value={selectedPedidosType}
                    onChange={setSelectedPedidosType}
                    placeholder="Tipo"
                  />
                </div>

                <div className="multiselect-container">
                  <CheckboxMultiSelect
                    options={productosOptions}
                    value={selectedProductos}
                    onChange={setSelectedProductos}
                    placeholder="Productos"
                  />
                </div>
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {loading ? (
        <LoadingSpinner text="Cargando Historial" transparent />
      ) : (
        <div className="overflow-x-auto mt-3">
          <Table 
            data={filteredPedidos} 
            pedidosTypeMap={pedidosTypeMap}
            onPedidoCancelled={handlePedidoCancelled}
          />
        </div>
      )}
    </div>
  );
};

export default HistorialPedidos;